import { Project } from '../components/ProjectCard';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '/api';

class ProjectService {
  // Get all projects for the user
  async getProjects(customerId?: string, status?: string, type?: string): Promise<Project[]> {
    try {
      const params = new URLSearchParams();
      if (customerId) params.append('customerId', customerId);
      if (status) params.append('status', status);
      if (type) params.append('type', type);

      const response = await fetch(`${API_BASE_URL}/api/projects?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('riskninja-token')}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch projects');
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching projects:', error);
      throw error;
    }
  }

  // Get a specific project with analytics
  async getProject(projectId: string): Promise<Project> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/projects/${projectId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('riskninja-token')}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch project');
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching project:', error);
      throw error;
    }
  }

  // Create a new project
  async createProject(projectData: Partial<Project> & { customerId: string; title: string; type: string }): Promise<Project> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/projects`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('riskninja-token')}`
        },
        body: JSON.stringify(projectData)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Failed to create project' }));
        throw new Error(errorData.error || 'Failed to create project');
      }

      return await response.json();
    } catch (error) {
      console.error('Error creating project:', error);
      throw error;
    }
  }

  // Update a project
  async updateProject(projectId: string, projectData: Partial<Project>): Promise<Project> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/projects/${projectId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('riskninja-token')}`
        },
        body: JSON.stringify(projectData)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Failed to update project' }));
        throw new Error(errorData.error || 'Failed to update project');
      }

      return await response.json();
    } catch (error) {
      console.error('Error updating project:', error);
      throw error;
    }
  }

  // Soft delete a project (sets status to cancelled)
  async softDeleteProject(projectId: string): Promise<{ message: string }> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/projects/${projectId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('riskninja-token')}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Failed to delete project' }));
        throw new Error(errorData.error || 'Failed to delete project');
      }

      return await response.json();
    } catch (error) {
      console.error('Error soft deleting project:', error);
      throw error;
    }
  }

  // Hard delete a project (permanently removes from database)
  async deleteProject(projectId: string): Promise<{ message: string; associatedDataMoved: number }> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/projects/${projectId}/permanent`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('riskninja-token')}`
        },
        body: JSON.stringify({ confirmDelete: 'delete' })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Failed to delete project' }));
        throw new Error(errorData.error || 'Failed to delete project');
      }

      return await response.json();
    } catch (error) {
      console.error('Error deleting project:', error);
      throw error;
    }
  }

  // Get project analytics
  async getProjectAnalytics(projectId: string): Promise<{
    summary: {
      documentCount: number;
      chatSessionCount: number;
      reportCount: number;
      workflowCount: number;
      completedWorkflows: number;
      activeWorkflows: number;
    };
    recentDocuments: any[];
    recentChats: any[];
    recentReports: any[];
    timeline: any[];
    policyTypes: Record<string, number>;
  }> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/projects/${projectId}/analytics`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('riskninja-token')}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch project analytics');
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching project analytics:', error);
      throw error;
    }
  }
}

export const projectService = new ProjectService();